import React, { useContext, useEffect, useState } from 'react';
import { assetUrl } from '~/helpers';
import useAnalytics from '~/react-hooks/use-analytics';
import styled from '@emotion/styled';
import Headline from '../Headline';
import AddMediaEmbed from '../Modals/AddMediaEmbed';
import Button, { BootstrapButton } from '~/App/Components/Button';
import colours from '~/utils/colours';
import EditorContext, { EditorContextProps } from '../../Pages/Editor/Context';
import { EmbedItem, EmbedType, Recording } from '~/types/models';
import EmbeddedItems from '../EmbeddedItems';
import { faPlus } from '@fortawesome/pro-solid-svg-icons';
import ChooseContentBlock from '../Modals/ChooseContentBlock';
import EmbedServicesList, {
  UnorderedList,
  IconWrapper,
} from '../EmbedServicesList';
import Tooltip, { themes } from '~/App/Components/Tooltip';
import VideoLibraryPicker from '../Modals/VideoLibraryPicker';
import { flatten } from '~/utils/Immutable';
import { Map, List, fromJS } from 'immutable';
import useSaveMessageTemplate from '../../Pages/Editor/hooks/useSaveMessageTemplate';
import { ContentBlockModalType } from '../../Types';
import PanelSection from '../PanelSection';
import ActionButton from '../ActionButton';
import {
  NEXTGEN_TEMPLATE_ADD_VIDEO,
  NEXTGEN_TEMPLATE_ADD_EMBED,
} from '~/generated/AnalyticsTypes';

const Wrapper = styled.div`
  padding-top: 10px;
`;

const ContentBlockDescription = styled.p`
  color: ${colours.greyDarkAlt3};
  margin-bottom: 15px !important;
`;

const AddVideoButton = styled(ActionButton)`
  color: ${colours.textDark};
  margin-bottom: 0;
  line-height: 40px;
`;

const AddMediaEmbedButton = styled(ActionButton)`
  color: #ffffff;
  margin-bottom: 0;
  line-height: 40px;
`;

const EmbeddedItemsWrapper = styled.div`
  position: relative;
  text-align: center;
`;

const StyledRoundAddContentBlockBtn = styled(Button)`
  width: 34px;
  height: 34px;
  border-radius: 100%;
  padding: 0;
  box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.5);
  display: block;
  outline: none;

  &:focus,
  &:active {
    outline: none;
  }

  .svg-inline--fa {
    margin-right: 0;
  }
`;

const StyledEmbedServicesList = styled(EmbedServicesList)`
  ${UnorderedList} {
    display: inline-flex;
    flex-wrap: wrap;
    align-items: end;
    justify-content: end;

    li {
      width: 20%;
    }

    ${IconWrapper} {
      width: 40px;
      height: 40px;

      img {
        max-width: 28px;
        max-height: 28px;
      }
    }

    @media (max-width: 1024px) {
      li {
        width: 25%;
      }
    }

    @media (max-width: 768px) {
      li {
        width: 20%;
      }
    }

    @media (max-width: 480px) {
      li {
        width: 33.3333%;
      }
    }
  }
`;

const Spacer = styled.div`
  padding: 10px;
`;

const TooltipWrapper = styled.div`
  margin-top: -15px;
  margin-bottom: 15px;

  span > span {
    cursor: default !important;
  }
`;

export const insertEmbedItem = (
  item: EmbedItem,
  items: List<Map<string, string>>
): List<Map<string, string>> => {
  const immutableItem = fromJS(flatten(item));

  if (item.type === EmbedType.Video) {
    let index = items.findLastKey(
      (embed) => embed?.get('type') === EmbedType.Video
    );
    index = index === undefined ? 0 : index + 1;

    return items.insert(index, immutableItem);
  }

  return items.push(immutableItem);
};

const ContentBlockTab = () => {
  const analytics = useAnalytics();

  const context: EditorContextProps = useContext(EditorContext);
  const embedItems =
    context.get<List<Map<string, string>>>('embeds') ??
    List<Map<string, string>>();
  const [activeModal, setActiveModal] = useState('');
  const { hasChanges } = context;

  const [selectedVideoIds, setSelectedVideoIds] = useState<string[]>([]);
  const [isSaving, saveMessageTemplate] = useSaveMessageTemplate(context);

  const addEmbedItem = (item: EmbedItem) => {
    context.set('embeds', insertEmbedItem(item, embedItems));
    setActiveModal('');

    if (item.type === EmbedType.Video) {
      analytics.track(NEXTGEN_TEMPLATE_ADD_VIDEO);
      return;
    }

    analytics.track(NEXTGEN_TEMPLATE_ADD_EMBED, {
      embedType: item.title,
    });
  };

  const removeEmbedItem = (index: number) => {
    if (embedItems.has(index)) {
      context.set('embeds', embedItems.remove(index));
    }
  };

  const showSelectedContentBlock = (modalName: ContentBlockModalType) => {
    switch (modalName) {
      case ContentBlockModalType.MediaEmbed:
        setActiveModal(ContentBlockModalType.MediaEmbed);
        break;
      case ContentBlockModalType.VideoLibrary:
        setActiveModal(ContentBlockModalType.VideoLibrary);
        break;
    }
  };

  const isVideoEmbedded = (item: EmbedItem) => {
    const index = embedItems.findIndex(
      (embed) =>
        item.type === EmbedType.Video &&
        embed?.get('model.id') === (item.model as Recording).id
    );

    return index !== -1;
  };

  const handleVideoSelected = (recording: Recording) => {
    const embedItem: EmbedItem = {
      type: EmbedType.Video,
      model: recording,
      model_id: recording.id,
      icon: assetUrl('/assets/images/message-template/video-library.svg'),
      title: recording.name,
    };

    if (!isVideoEmbedded(embedItem)) {
      addEmbedItem(embedItem);
    }

    setActiveModal('');
  };

  const handleVideoDeleted = (recording: Recording) => {
    const index = embedItems.findIndex(
      (item) =>
        item?.get('type') === EmbedType.Video &&
        item.get('model.id') === recording.id
    );

    if (index !== -1) {
      removeEmbedItem(index);
    }
  };

  const handlePreviewButtonClick = (e: React.MouseEvent<BootstrapButton>) => {
    e.preventDefault();

    if (hasChanges) {
      saveMessageTemplate();
    } else {
      window.open(`/message-template/${context.get('id')}/preview`);
    }
  };

  const commonModalProps = {
    isOpen: true,
    hideActions: true,
    onRequestClose: () => setActiveModal(''),
  };
  const modals = {};
  modals[ContentBlockModalType.MediaEmbed] = (
    <AddMediaEmbed
      {...commonModalProps}
      onEmbedAdded={(item) => addEmbedItem(item)}
    />
  );
  modals[ContentBlockModalType.VideoLibrary] = (
    <VideoLibraryPicker
      {...commonModalProps}
      selectedVideoIds={selectedVideoIds}
      onClick={handleVideoSelected}
      onDeleted={handleVideoDeleted}
    />
  );
  modals[ContentBlockModalType.ChooseContentBlock] = (
    <ChooseContentBlock
      {...commonModalProps}
      onContentBlockSelected={showSelectedContentBlock}
    />
  );

  useEffect(() => {
    if (embedItems) {
      const ids: string[] = [];

      embedItems?.map((item) => {
        if (item?.get('type') === EmbedType.Video) {
          ids.push(item.get('model.id'));
        }
      });

      setSelectedVideoIds(ids);
    }
  }, [embedItems]);

  return (
    <Wrapper>
      <ContentBlockDescription>
        Content blocks allow you to add video or media from your favourite tools
        in any order you like.
      </ContentBlockDescription>
      {!embedItems?.size && (
        <>
          <PanelSection>
            <Headline
              imageIcon={assetUrl(
                'assets/images/message-template/AddVideo.svg'
              )}
              title='Video sequence'
              helpContent='Add all your favourite videos from your Bonjoro My Library.'
              id='video-sequence'
            />
            <AddVideoButton
              bsStyle='important'
              block
              onClick={() => setActiveModal(ContentBlockModalType.VideoLibrary)}
            >
              Add video
            </AddVideoButton>
          </PanelSection>

          <PanelSection>
            <Headline
              imageIcon={assetUrl('assets/images/message-template/Embed.svg')}
              title='Media embed'
              id='media-embed'
              tooltipHideDelay={4000}
              helpContent={
                <>
                  Copy an Embed or iFrame link from any of the below options.
                  Come back here to add and paste. Need help?{' '}
                  <a
                    href='https://help.bonjoro.com/en/articles/5404409-next-gen-message-templates'
                    rel='noreferrer'
                    target='_blank'
                  >
                    See our Embed guide.
                  </a>
                </>
              }
            />
            <AddMediaEmbedButton
              bsStyle='primary'
              block
              onClick={() => setActiveModal(ContentBlockModalType.MediaEmbed)}
            >
              Add media
            </AddMediaEmbedButton>
          </PanelSection>

          <StyledEmbedServicesList />
        </>
      )}
      {embedItems && embedItems.size > 0 && (
        <EmbeddedItemsWrapper>
          <EmbeddedItems
            items={embedItems}
            onRemoveItemClick={removeEmbedItem}
          />
          {embedItems.size < 3 ? (
            <TooltipWrapper>
              <Tooltip
                id='add-embed-tooltip'
                placement='right'
                body='Add content block'
                opacity={1}
                theme={themes.black}
              >
                <div>
                  <StyledRoundAddContentBlockBtn
                    bsStyle='important'
                    icon={faPlus}
                    onClick={() =>
                      setActiveModal(ContentBlockModalType.ChooseContentBlock)
                    }
                  />
                </div>
              </Tooltip>
            </TooltipWrapper>
          ) : (
            <Spacer />
          )}

          <Button
            block
            bsStyle='secondary'
            loading={isSaving}
            disabled={isSaving}
            onClick={handlePreviewButtonClick}
          >
            {hasChanges ? 'Save to preview' : 'Preview'}
          </Button>
        </EmbeddedItemsWrapper>
      )}

      {modals[activeModal]}
    </Wrapper>
  );
};

export default ContentBlockTab;
